import useSWR from "swr";
import { useCallback, useMemo } from "react";

import axios, { fetcher, endpoints } from "src/utils/axios";

export type TeamCreatePayload = {
  name: string;
  sport: string;
  address?: string;
  contact_email?: string;
  contact_phone?: string;
};

export type TeamUpdatePayload = {
  name?: string;
  address?: string;
  contact_email?: string;
  contact_phone?: string;
  singles_order?: number[];
  doubles_order?: number[];
  sport: string;
};

export type TeamInviteByEmailPayload = {
  email: string;
};

export type TeamActionResponse = {
  detail: string;
  team_id: number;
  player_user_id?: number;
  invited_email?: string;
};

export async function createTeam(
  providerId: number,
  payload: TeamCreatePayload,
  isProvider: boolean
) {
  const url = isProvider
    ? endpoints.provider.teams.byProvider(providerId)
    : endpoints.team.create(providerId);

  return await axios.post(url, payload);
}

export async function updateTeam(
  providerId: number,
  teamId: number,
  payload: TeamUpdatePayload,
  isProvider: boolean
) {
  const url = isProvider
    ? endpoints.provider.teams.byId(providerId, teamId)
    : endpoints.team.byId(providerId, teamId);

  return await axios.post(url, payload);
}

export async function invitePlayerToTeam(
  providerId: number,
  teamId: number,
  payload: TeamInviteByEmailPayload,
  isProvider: boolean
) {
  const url = isProvider
    ? endpoints.provider.teams.invite(providerId, teamId)
    : endpoints.team.invite(providerId, teamId);

  return await axios.post(url, payload);
}

export async function addProviderPlayerToTeamByEmail(
  providerId: number,
  teamId: number,
  payload: TeamInviteByEmailPayload
) {
  return await axios.post(
    endpoints.provider.teams.addPlayerByEmail(providerId, teamId),
    payload
  );
}

export async function removePlayerFromTeam(
  providerId: number,
  teamId: number,
  playerUserId: number,
  isProvider: boolean
) {
  const url = isProvider
    ? endpoints.provider.teams.removePlayer(providerId, teamId, playerUserId)
    : endpoints.team.removePlayer(providerId, teamId, playerUserId);

  return await axios.delete(url);
}

export function useGetProviderTeams(
  providerId?: number,
  sport?: string,
  q?: string
) {
  const params = new URLSearchParams();

  if (sport?.trim()) {
    params.set("sport", sport.trim());
  }

  if (q?.trim()) {
    params.set("q", q.trim());
  }

  const query = params.toString();

  const URL =
    providerId && providerId > 0
      ? `${endpoints.provider.teams.root}/${providerId}/teams${
          query ? `?${query}` : ""
        }`
      : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);

  const refetchProviderTeams = useCallback(() => mutate(), [mutate]);

  const memoizedValue = useMemo(
    () => ({
      providerTeams: data ?? [],
      providerTeamsLoading: isLoading,
      providerTeamsError: error,
      providerTeamsValidating: isValidating,
      providerTeamsEmpty: !isLoading && (!data || data.length === 0),
      refetchProviderTeams,
    }),
    [data, error, isLoading, isValidating, refetchProviderTeams]
  );

  return memoizedValue;
}

export async function deleteTeam(
  providerId: number,
  teamId: number,
  isProvider: boolean
) {
  const url = isProvider
    ? endpoints.provider.teams.remove(providerId, teamId)
    : endpoints.team.remove(providerId, teamId);

  return await axios.delete(url);
}

export async function setTeamCaptain(
  providerId: number,
  teamId: number,
  payload: {
    captain_number: 1 | 2;
    user_id: number;
  }
) {
  return axios.post(
    endpoints.provider.teams.setCaptain(providerId, teamId),
    payload
  );
}

export function useGetMyTeams(userId?: number) {
  const URL = userId && userId > 0 ? `${endpoints.team.myTeams}` : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);

  const refetchMyTeams = useCallback(() => mutate(), [mutate]);

  const memoizedValue = useMemo(
    () => ({
      myTeams: data ?? [],
      myTeamsLoading: isLoading,
      myTeamsError: error,
      myTeamsValidating: isValidating,
      myTeamsEmpty: !isLoading && (!data || data.length === 0),
      refetchMyTeams,
    }),
    [data, error, isLoading, isValidating, refetchMyTeams]
  );

  return memoizedValue;
}

export interface IPublicTournamentCategoryTeamItem {
  id: number;
  name: string;
  sport: string;
  actual_player_count: number;
  max_team_size: number | null;
  can_apply: boolean;
}

export async function fetchTournamentCategoryTeams(
  providerId: number,
  tournamentId: number,
  categoryId: number
): Promise<IPublicTournamentCategoryTeamItem[]> {
  const res = await axios.get(
    endpoints.user.team.tournamentCategoryTeams(
      providerId,
      tournamentId,
      categoryId
    )
  );

  return res.data;
}

export async function createPlayerSelfInvitationToTeam(
  teamId: number | string,
  email: string
) {
  //return axios.post(`/playerinvite/teams/${teamId}`, { email });
  return axios.post(endpoints.user.team.selfInvite(teamId), { email });
}
